/**
 * Design tokens — Figma Employer Portal (SEPTEMBER).
 * Primary: #2a9699 · Sidebar: #2a9699 / #006d70
 */

export const THEME_MODES = ["light"] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

export const BREAKPOINTS = {
	xs: 0,
	sm: 640,
	md: 768,
	lg: 1024,
	xl: 1280,
	"2xl": 1536,
} as const;

export type BreakpointKey = keyof typeof BREAKPOINTS;

/** Expanded sidebar width — matches institute portal (`sidebarSlice.expandedWidth`). */
export const SIDEBAR_EXPANDED_WIDTH_PX = 228;

const shared = {
	typography: {
		fontFamily: {
			sans: '"Poppins", sans-serif',
			mono: "ui-monospace, 'Cascadia Code', 'Segoe UI Mono', monospace",
		},
		fontSize: {
			xs: "0.75rem",
			sm: "0.875rem",
			base: "1rem",
			lg: "1.125rem",
			xl: "1.25rem",
			"2xl": "1.5rem",
			"3xl": "1.875rem",
			"4xl": "2.25rem",
		},
		fontWeight: {
			normal: 400,
			medium: 500,
			semibold: 600,
			bold: 700,
		},
		lineHeight: {
			tight: 1.25,
			normal: 1.5,
			relaxed: 1.625,
		},
	},
	spacing: {
		0: "0",
		1: "0.25rem",
		2: "0.5rem",
		3: "0.75rem",
		4: "1rem",
		5: "1.25rem",
		6: "1.5rem",
		8: "2rem",
		10: "2.5rem",
		12: "3rem",
		16: "4rem",
	},
	radius: {
		none: "0",
		sm: "0.375rem",
		md: "0.5rem",
		lg: "0.75rem",
		xl: "1rem",
		full: "9999px",
	},
	shadow: {
		sm: "0 1px 2px 0 rgb(15 23 42 / 0.05)",
		card: "0 4px 97px 0 rgb(0 0 0 / 0.05)",
		md: "0 4px 6px -1px rgb(15 23 42 / 0.08), 0 2px 4px -2px rgb(15 23 42 / 0.08)",
		lg: "0 10px 15px -3px rgb(15 23 42 / 0.08), 0 4px 6px -4px rgb(15 23 42 / 0.08)",
	},
	zIndex: {
		base: 0,
		dropdown: 20,
		sidebar: 30,
		header: 40,
		modal: 50,
		snackbar: 60,
		tooltip: 70,
	},
	motion: {
		durationFast: "150ms",
		durationNormal: "250ms",
		durationSlow: "350ms",
		easingDefault: "cubic-bezier(0.4, 0, 0.2, 1)",
	},
	layout: {
		sidebarWidth: `${SIDEBAR_EXPANDED_WIDTH_PX}px`,
		headerHeight: "3.5rem",
		footerHeight: "3.25rem",
		containerSm: "48rem",
		containerMd: "64rem",
		containerLg: "80rem",
	},
} as const;

/** Employer brand primary — use with Tailwind `text-primary` / `bg-primary` and form controls. */
export const brandPrimary = "#2a9699";
export const brandPrimaryHover = "#248a8c";
export const brandPrimaryMuted = "rgba(42, 150, 153, 0.1)";

/** Captcha display box (aligned with institute portal). */
export const captchaDisplayBorder = "#D9D9D9";
export const captchaDisplayBorderWidth = 1.2;
export const captchaDisplayRadius = 8;
export const captchaDisplayBackground =
	"linear-gradient(135deg, #EEF2F7 0%, #E8EDF5 100%)";

const lightColors = {
	primary: brandPrimary,
	primaryForeground: "#ffffff",
	primaryHover: brandPrimaryHover,
	secondary: "#e1f0f5",
	secondaryForeground: "#006d70",
	background: "#f2f4f8",
	foreground: "#161616",
	surface: "#ffffff",
	surfaceForeground: "#161616",
	surfaceMuted: "#f7f7f7",
	muted: "#494949",
	mutedForeground: "#8a8a8a",
	border: "#d9d9d9",
	borderStrong: "#e7e7e7",
	danger: "#ff6e4f",
	dangerForeground: "#ffffff",
	success: "#006b5e",
	successForeground: "#ffffff",
	warning: "#f59e0b",
	warningForeground: "#ffffff",
	info: "#026cb5",
	infoForeground: "#ffffff",
	focusRing: "#2a9699",
	overlay: "rgb(15 23 42 / 0.5)",
	primarySoft: "#e1f0f5",
	primarySoftHover: "#d4eaf0",
	primarySoftBorder: "#c5e3ea",
	primarySoftText: "#006d70",
} as const;

/** Semantic status pill colors (e.g. dashboard application status) */
export const statusTokens = {
	warningSurface: "#fff4df",
	warningText: "#9a6700",
	warningAccent: "#e2a62f",
} as const;

const componentTokens = {
	button: {
		heightSm: "2.25rem",
		heightMd: "2.5rem",
		heightLg: "3.375rem",
		radius: "5px",
		fontWeight: shared.typography.fontWeight.semibold,
	},
	input: {
		height: "3.125rem",
		radius: "0.625rem",
	},
	focusRingWidth: "2px",
	focusRingOffset: "2px",
} as const;

export const statCardTokens = {
	blue: { accent: "#2a9699", value: "#161616", surface: "#e1f0f5" },
	purple: { accent: "#8b5cf6", value: "#161616", surface: "#f7e3ff" },
	coral: { accent: "#ff6e4f", value: "#161616", surface: "#ffe2db" },
	rejected: { accent: "#ff6e4f", value: "#161616", surface: "#fdecea" },
} as const;

export type StatCardVariant = keyof typeof statCardTokens;

/** Dashboard chrome — sidebar / header shell */
export const shellTokens = {
	background: "#2a9699",
	backgroundElevated: "#006d70",
	foreground: "#ffffff",
	muted: "rgba(255, 255, 255, 0.75)",
	border: "rgba(152, 213, 255, 0.35)",
	navIcon: "#ffffff",
	navActive: "#ffffff",
	navActiveBg: "rgba(255, 255, 255, 0.12)",
	headerBackground: "#f0ffff",
	headerForeground: "#343434",
	headerBorder: "#e7e7e7",
} as const;

/** Auth panel accent words */
export const authAccentTokens = {
	knowledge: "#74c6ff",
	skills: "#ff7faa",
	behavior: "#2a9699",
	heading: "#161616",
} as const;

export const themeTokens = {
	shared,
	components: componentTokens,
	statCard: statCardTokens,
	status: statusTokens,
	shell: shellTokens,
	auth: authAccentTokens,
	light: { colors: lightColors },
} as const;

export type ThemeColorToken = keyof typeof lightColors;
export type ThemeColors = { readonly [K in ThemeColorToken]: string };

export function resolveThemeColors(mode: ThemeMode = "light"): ThemeColors {
	return themeTokens[mode].colors;
}
